import _ from "lodash";
import React, { Fragment, useMemo } from "react";
import classnames from "classnames";
import { FormattedMessage } from "react-intl";

import { UnstyledButton } from "@sportal/cdk/unstyledButton/UnstyledButton";
import { Mobile, NotMobile } from "../../../hocs/Responsive";
import { Select } from "../../select";
import NativeSelect from "../../select/NativeSelect";
import { Icon } from "../../icon/Icon";
import { useIntlTranslate } from "../../../hooks";

import "./Pagination.scss";

const PAGINATION_OPTIONS = ["25", "50", "100"];

export const Pagination = ({
  canNextPage,
  canPreviousPage,
  gotoPage,
  nextPage,
  pageCount,
  previousPage,
  rows,
  setPageSize,
  state,
}) => {
  function handlePageSizeChange(value) {
    setPageSize(+value);
    gotoPage(0);
  }

  return (
    <div className={"pagination"}>
      <PaginationMessage
        rowsCount={rows.length}
        onPageSizeChange={handlePageSizeChange}
        state={state}
      />
      <PaginationControls
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        gotoPage={gotoPage}
        nextPage={nextPage}
        pageCount={pageCount}
        previousPage={previousPage}
        state={state}
      />
    </div>
  );
};

export const PaginationMessage = ({
  onPageSizeChange,
  rowsCount,
  state: { pageIndex, pageSize },
}) => {
  const translate = useIntlTranslate();

  const listOfSizes = useMemo(() => {
    return PAGINATION_OPTIONS.map((option) => ({
      value: option,
      name: option,
    }));
  }, []);

  const items = useMemo(() => {
    return PAGINATION_OPTIONS.map((option) => ({
      value: option,
      content: option,
    }));
  }, []);

  const userPage = pageIndex + 1;
  const entries = rowsCount;
  const start = userPage * pageSize - pageSize + 1;
  const end = userPage * pageSize > entries ? entries : userPage * pageSize;

  const page = rowsCount !== 0 ? `${start} - ${end}` : 0; // 0 should be displayed when table has no records
  const pageValues = { page, entries };

  return (
    <div className={"pagination__message"}>
      <div className={"pagination__rows-select"}>
        <span id="rows-per-page-label">
          <FormattedMessage id={"rows_per_page"} />
        </span>
        <NotMobile>
          <Select
            items={items}
            selected={`${pageSize}`}
            onChange={onPageSizeChange}
            ariaLabel={translate("rows_per_page")}
            small
          />
        </NotMobile>
        <Mobile>
          <NativeSelect
            options={listOfSizes}
            selected={`${pageSize}`}
            className={"pagination__rows-select__select"}
            toggleView={"link"}
            translateKey="amount"
            onChange={onPageSizeChange}
            ariaLabelledBy="rows-per-page-label"
            small
          />
        </Mobile>
      </div>
      <span data-testid="pagination-info">
        <NotMobile>
          <FormattedMessage
            id={"showing_amount_of_table_items"}
            values={pageValues}
          />
        </NotMobile>
        <Mobile>
          <FormattedMessage id={"amount_of_table_items"} values={pageValues} />
        </Mobile>
      </span>
    </div>
  );
};

const PaginationListItem = ({ children, ...attrs }) => {
  return (
    <li className="pagination-list__item" {...attrs}>
      {children}
    </li>
  );
};

export const PaginationControls = ({
  canNextPage,
  canPreviousPage,
  gotoPage,
  nextPage,
  pageCount,
  previousPage,
  state: { pageIndex },
}) => {
  const translate = useIntlTranslate();

  return (
    <nav
      className={"pagination__controls"}
      aria-label={translate("table_pagination_label")}
    >
      <ul className="pagination-list">
        <PaginationListItem>
          <UnstyledButton
            className="prev"
            disabled={!canPreviousPage}
            onClick={() => previousPage()}
            aria-label={translate("table_pagination_previous_page")}
          >
            <Icon icon="fa-regular fa-angle-left" />
          </UnstyledButton>
        </PaginationListItem>
        <PaginationPages
          pageCount={pageCount}
          activePageIndex={pageIndex}
          gotoPage={gotoPage}
        />
        <PaginationListItem>
          <UnstyledButton
            className="next"
            disabled={!canNextPage}
            onClick={() => nextPage()}
            aria-label={translate("table_pagination_next_page")}
          >
            <Icon icon="fa-regular fa-angle-right" />
          </UnstyledButton>
        </PaginationListItem>
      </ul>
    </nav>
  );
};

const Page = ({ pageIndex, activePageIndex, gotoPage }) => {
  const translate = useIntlTranslate();

  const pageNumber = pageIndex + 1;
  const isActive = pageIndex === activePageIndex;

  return (
    <PaginationListItem>
      <UnstyledButton
        aria-label={translate("table_pagination_page_label", {
          number: pageNumber,
        })}
        {...(isActive && {
          "aria-current": true,
          tabIndex: -1,
        })}
        className={classnames("page", {
          "page--active": isActive,
        })}
        onClick={() => gotoPage(pageIndex)}
      >
        {pageNumber}
      </UnstyledButton>
    </PaginationListItem>
  );
};

const PaginationEllipsis = () => {
  return (
    <PaginationListItem role="presentation">
      <span className="pagination-ellipsis">…</span>
    </PaginationListItem>
  );
};

const PaginationPages = ({ pageCount, activePageIndex, gotoPage }) => {
  const first = 0;

  if (!pageCount) {
    return (
      <Page
        pageIndex={first}
        activePageIndex={activePageIndex}
        gotoPage={gotoPage}
      />
    );
  }

  if (pageCount < 5) {
    return (
      <Fragment>
        {_.times(pageCount, (pageIndex) => (
          <Page
            key={pageIndex}
            pageIndex={pageIndex}
            activePageIndex={activePageIndex}
            gotoPage={gotoPage}
          />
        ))}
      </Fragment>
    );
  }

  const last = pageCount - 1;

  let second = activePageIndex > 1 ? activePageIndex : 1;
  let third = second + 1;

  if (activePageIndex === last || activePageIndex === last - 1) {
    third = last - 1;
    second = third - 1;
  }

  return (
    <Fragment>
      <Page
        pageIndex={first}
        activePageIndex={activePageIndex}
        gotoPage={gotoPage}
      />
      {activePageIndex > 1 && <PaginationEllipsis />}
      <Page
        pageIndex={second}
        activePageIndex={activePageIndex}
        gotoPage={gotoPage}
      />
      <Page
        pageIndex={third}
        activePageIndex={activePageIndex}
        gotoPage={gotoPage}
      />
      {activePageIndex < last - 2 && <PaginationEllipsis />}
      <Page
        pageIndex={last}
        activePageIndex={activePageIndex}
        gotoPage={gotoPage}
      />
    </Fragment>
  );
};
